import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DatePicker, DateValidationError, LocalizationProvider } from '@mui/x-date-pickers';
import {
  downloadAuditTrail,
  downloadAuditTrailOrg,
  requestFullAuditExport,
  requestCertifiedAuditPdf,
} from 'store/myAccount/requests';
import { useMutation, useQuery } from '@redux-requests/react';
import { GVTypography } from 'components/lib';
import { Formik } from 'formik';
import classnames from 'classnames/bind';
import { Download, Mail, Calendar } from '@globalvisioninc/assets/icons';
import { Spinner } from '@globalvisioninc/ui/spinner';
import { Button } from '@globalvisioninc/ui/button';
import { FieldLabel } from '@globalvisioninc/ui/field-label';
import { RadioGroup } from '@globalvisioninc/ui/radio-group';
import { SegmentedControl } from '@globalvisioninc/ui/segmented-control';
import { Separator } from '@globalvisioninc/ui/separator';
import { app } from 'store';
import { differenceInDays, isAfter, format } from 'date-fns';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import MyAccountCard from '../Card';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { getTrackerMetadata } from 'store/trackerMetadata/trackerMetadata';
import { useOrganizationDetailsQuery } from 'store/queries/organizations/useOrganizationQuery';
import {
  type AuditExportScope,
  type DateRangeExportFormat,
  ctaTooltip,
  CERTIFIED_PDF_FORMAT_TOOLTIP,
  CSV_FORMAT_TOOLTIP,
  isEmailExport,
  primaryCtaLabel,
  resolveExportLatchAfterMutations,
  toAuditTrailExportRange,
} from './auditTrailExportUi';
import styles from './General.module.css';

const cx = classnames.bind(styles);

const FOUR_MONTHS_IN_DAYS = 120;
const MAX_DAYS_IN_RANGE = FOUR_MONTHS_IN_DAYS;
const TOOLTIP_DELAY_DURATION = 500;

const year2019Limit = dayjs('2019-01-01');

const General = () => {
  const [startValue, setStartValue] = React.useState<Dayjs | null>(null);
  const [startError, setStartError] = React.useState<DateValidationError | null>(null);
  const [startOpen, setStartOpen] = React.useState(false);
  const [endValue, setEndValue] = React.useState<Dayjs | null>(null);
  const [endError, setEndError] = React.useState<DateValidationError | null>(null);
  const [endOpen, setEndOpen] = React.useState(false);
  const [tooltipContainer, setTooltipContainer] = React.useState<HTMLDivElement | null>(null);
  const [scope, setScope] = React.useState<AuditExportScope>('dateRange');
  const [formatKind, setFormatKind] = React.useState<DateRangeExportFormat>('csv');
  const [exportRequested, setExportRequested] = React.useState(false);
  const dispatch = useDispatch();
  const { loading: loadingGetFile } = useQuery(downloadAuditTrail);
  const { loading: loadingGetFileOrg } = useQuery(downloadAuditTrailOrg);
  const { loading: loadingRequestFullHistory, error: requestFullAuditExportError } =
    useMutation(requestFullAuditExport);
  const { loading: loadingRequestCertifiedPdf, error: requestCertifiedAuditPdfError } =
    useMutation(requestCertifiedAuditPdf);
  const loadingEmailExport = loadingRequestFullHistory || loadingRequestCertifiedPdf;
  const loadingInstantCsv = loadingGetFile || loadingGetFileOrg;
  const showFormatToggle = scope === 'dateRange';
  const emailExport = isEmailExport(scope, formatKind);
  const ctaBusy = emailExport ? loadingEmailExport : loadingInstantCsv;
  const ctaLabel = primaryCtaLabel(scope, formatKind);
  const wasFullHistoryLoading = React.useRef(false);
  const wasCertifiedPdfLoading = React.useRef(false);

  const trackerMetadata = useSelector(getTrackerMetadata);
  const orgId = trackerMetadata?.orgId;

  const {
    data: organizationData,
    isLoading: isLoadingOrganization,
    isError: isErrorOrganization,
  } = useOrganizationDetailsQuery(orgId!, {});

  useEffect(() => {
    if (organizationData) {
    }
  }, [organizationData]);

  useEffect(() => {
    const csvJustSettled = wasFullHistoryLoading.current && !loadingRequestFullHistory;
    const pdfJustSettled = wasCertifiedPdfLoading.current && !loadingRequestCertifiedPdf;
    wasFullHistoryLoading.current = Boolean(loadingRequestFullHistory);
    wasCertifiedPdfLoading.current = Boolean(loadingRequestCertifiedPdf);

    const nextLatch = resolveExportLatchAfterMutations({
      csvJustSettled,
      pdfJustSettled,
      csvError: requestFullAuditExportError,
      pdfError: requestCertifiedAuditPdfError,
    });
    if (nextLatch === 'release') setExportRequested(false);
  }, [
    loadingRequestFullHistory,
    loadingRequestCertifiedPdf,
    requestFullAuditExportError,
    requestCertifiedAuditPdfError,
  ]);

  const today = new Date();

  const minDateAllowed = React.useMemo(() => {
    const orgCreatedAt = organizationData?.createdAt;
    let effectiveMinDate = year2019Limit;

    if (orgCreatedAt) {
      const orgCreationDayjs = dayjs(orgCreatedAt).startOf('day');
      if (orgCreationDayjs.isValid()) {
        if (orgCreationDayjs.isAfter(year2019Limit)) {
          effectiveMinDate = orgCreationDayjs;
        }
      }
    }
    return effectiveMinDate;
  }, [organizationData]);

  const effectiveIsLoading = !orgId || isLoadingOrganization;

  const emptyField = !startValue || !endValue;
  const rangeInFutur =
    (startValue && isAfter(startValue.toDate(), today)) || (endValue && isAfter(endValue.toDate(), today));
  const invalidRange = startValue && endValue && startValue > endValue;
  const invalidMaxRange =
    startValue && endValue && differenceInDays(endValue.toDate(), startValue.toDate()) > MAX_DAYS_IN_RANGE;
  const invalidMinDate = minDateAllowed ? !!startValue && startValue.isBefore(minDateAllowed) : false;
  const rangeInvalid = !!(emptyField || rangeInFutur || invalidRange || invalidMaxRange || invalidMinDate);
  const dateRangeActionsDisabled =
    effectiveIsLoading || (isErrorOrganization && !!orgId) || rangeInvalid || loadingGetFile || loadingGetFileOrg;

  const errorDisplayMessage: { [key: string]: string } = {
    invalidDate: 'Invalid Date',
    emptyField: 'Required',
    disableFuture: 'Not future date',
    invalidRange: 'Invalid range, Start date must be smaller than end date.',
    invalidMaxRange: `The date range should not exceed the limit of ${MAX_DAYS_IN_RANGE} days.`,
    minDate: minDateAllowed
      ? `Min date is ${minDateAllowed.format('MM/DD/YYYY')}`
      : 'Date cannot be before organization creation.',
    default: '',
  };

  const calendarIcon = () => <Calendar data-testid="CalendarTodayIcon" size={18} strokeWidth={1.125} aria-hidden />;

  const applyScope = (next: string | null) => {
    if (next !== 'dateRange' && next !== 'fullHistory') return;
    setScope(next);
    if (next === 'fullHistory') {
      setStartValue(null);
      setEndValue(null);
      setStartError(null);
      setEndError(null);
    }
  };

  let subtitle = null;
  let cardBody: React.ReactElement;

  if (!orgId && !isLoadingOrganization) {
    cardBody = (
      <GVTypography emphasis="medium" variant="subtitle1">
        Organization ID not found. Unable to determine date constraints.
      </GVTypography>
    );
  } else if (effectiveIsLoading) {
    cardBody = <Spinner.Root className={styles.loading} />;
  } else if (isErrorOrganization && orgId) {
    cardBody = (
      <GVTypography emphasis="medium" variant="subtitle1">
        Error fetching organization details. Date constraints may be inaccurate or using default.
      </GVTypography>
    );
  } else {
    const startHelperText = errorDisplayMessage[startError || 'default'] || '';
    const endHelperText = errorDisplayMessage[endError || 'default'] || '';
    const rangeErrorText =
      (invalidRange && errorDisplayMessage.invalidRange) ||
      (invalidMaxRange && errorDisplayMessage.invalidMaxRange) ||
      '';
    const tooltip = ctaTooltip({
      scope,
      format: formatKind,
      exportRequested,
      datesIncomplete: emptyField,
    });
    const ctaDisabled =
      effectiveIsLoading ||
      (isErrorOrganization && !!orgId) ||
      ctaBusy ||
      (scope === 'dateRange' && dateRangeActionsDisabled) ||
      (emailExport && exportRequested);

    subtitle = <p className={styles.subtitle}>Choose which events to export:</p>;
    cardBody = (
      <Formik
        key="settingsForm"
        initialValues={{}}
        validate={() => {
          if (scope === 'fullHistory') return;
          let message;
          if (emptyField) {
            message = 'All fields are required.';
          }
          if (rangeInFutur) {
            message = 'Start date and end date should not be greater than today.';
          }
          if (invalidRange) {
            message = 'Invalid range, Start date must be smaller than end date.';
          }
          if (startError === 'invalidDate' || endError === 'invalidDate') {
            message = 'Invalid Date, Try to choose a valid date.';
          }
          if (invalidMaxRange) {
            message = `The date range should not exceed the limit of ${MAX_DAYS_IN_RANGE} days.`;
          }
          if (invalidMinDate && minDateAllowed) {
            message = `Start date cannot be earlier than ${minDateAllowed.format('MM/DD/YYYY')}.`;
          }
          if (minDateAllowed && !startValue && endValue && endValue.isBefore(minDateAllowed)) {
            message = `End date cannot be earlier than ${minDateAllowed.format('MM/DD/YYYY')}.`;
          }
          if (message) {
            dispatch(
              app.actions.setSnackMessage({
                message,
                type: 'error',
              }),
            );
          }
        }}
        onSubmit={() => {
          if (scope === 'fullHistory') {
            dispatch(requestFullAuditExport());
            setExportRequested(true);
            return;
          }
          if (rangeInvalid || !startValue || !endValue) return;
          const { utcSince, utcUntil, sinceDate, untilDate } = toAuditTrailExportRange(
            startValue.toDate(),
            endValue.toDate(),
          );
          if (formatKind === 'pdf') {
            dispatch(requestCertifiedAuditPdf(utcSince, utcUntil, sinceDate, untilDate));
            setExportRequested(true);
            return;
          }
          dispatch(
            downloadAuditTrailOrg(
              utcSince,
              utcUntil,
              format(startValue.toDate(), 'dd-MM-yyyy'),
              format(endValue.toDate(), 'dd-MM-yyyy'),
            ),
          );
        }}
      >
        {({ handleSubmit }) => (
          <>
            <RadioGroup.Root
              orientation="horizontal"
              value={scope}
              onValueChange={applyScope}
              aria-label="Export scope"
            >
              <RadioGroup.Item value="dateRange">
                <RadioGroup.Button>
                  <RadioGroup.Indicator />
                </RadioGroup.Button>
                <RadioGroup.Label>Specific date range</RadioGroup.Label>
              </RadioGroup.Item>
              <RadioGroup.Item value="fullHistory">
                <RadioGroup.Button>
                  <RadioGroup.Indicator />
                </RadioGroup.Button>
                <RadioGroup.Label>
                  Full history <span className={styles.formatLabel}>(CSV)</span>
                </RadioGroup.Label>
              </RadioGroup.Item>
            </RadioGroup.Root>
            {showFormatToggle && (
              <>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <div className={styles.dates}>
                    <DatePicker
                      minDate={minDateAllowed}
                      label={'Start'}
                      format={'DD/MM/YYYY'}
                      disableHighlightToday
                      disableFuture
                      showDaysOutsideCurrentMonth
                      value={startValue}
                      open={startOpen}
                      onOpen={() => {
                        setStartOpen(true);
                        setEndOpen(false);
                      }}
                      onClose={() => setStartOpen(false)}
                      slots={{ openPickerIcon: calendarIcon }}
                      slotProps={{
                        day: { showDaysOutsideCurrentMonth: true },
                        textField: {
                          helperText: <span>{startHelperText}</span>,
                          error: invalidRange || invalidMaxRange || !!startError,
                          onClick: () => {
                            setStartOpen(true);
                            setEndOpen(false);
                          },
                          InputLabelProps: { shrink: Boolean(startValue) },
                          InputProps: { notched: false },
                        },
                      }}
                      onChange={(val) => setStartValue(val as dayjs.Dayjs | null)}
                      onError={(error) => setStartError(error)}
                    />
                    <span className={styles.to}>to</span>
                    <DatePicker
                      minDate={minDateAllowed}
                      disableHighlightToday
                      label={'End'}
                      format={'DD/MM/YYYY'}
                      disableFuture
                      showDaysOutsideCurrentMonth
                      value={endValue}
                      open={endOpen}
                      onOpen={() => {
                        setEndOpen(true);
                        setStartOpen(false);
                      }}
                      onClose={() => setEndOpen(false)}
                      slots={{ openPickerIcon: calendarIcon }}
                      slotProps={{
                        day: { showDaysOutsideCurrentMonth: true },
                        textField: {
                          helperText: <span>{endHelperText}</span>,
                          error: invalidRange || invalidMaxRange || !!endError,
                          onClick: () => {
                            setEndOpen(true);
                            setStartOpen(false);
                          },
                          InputLabelProps: { shrink: Boolean(endValue) },
                          InputProps: { notched: false },
                        },
                      }}
                      onChange={(val) => setEndValue(val as dayjs.Dayjs | null)}
                      onError={(error) => setEndError(error)}
                    />
                  </div>
                </LocalizationProvider>
                <div className={styles.format}>
                  <FieldLabel.Root htmlFor="audit-export-format" id="audit-export-format-label">
                    Export format
                  </FieldLabel.Root>
                  <SegmentedControl.Root
                    id="audit-export-format"
                    aria-labelledby="audit-export-format-label"
                    value={formatKind}
                    onValueChange={(next) => {
                      if (next === 'csv' || next === 'pdf') setFormatKind(next);
                    }}
                  >
                    <SegmentedControl.Item type="button" value="csv" data-testid="csv-format">
                      <GVTooltip
                        title={CSV_FORMAT_TOOLTIP}
                        delayDuration={TOOLTIP_DELAY_DURATION}
                        side="top"
                        container={tooltipContainer}
                      >
                        <span>CSV</span>
                      </GVTooltip>
                    </SegmentedControl.Item>
                    <SegmentedControl.Item type="button" value="pdf" data-testid="certified-pdf-format">
                      <GVTooltip
                        title={CERTIFIED_PDF_FORMAT_TOOLTIP}
                        delayDuration={TOOLTIP_DELAY_DURATION}
                        side="top"
                        container={tooltipContainer}
                      >
                        <span>Certified PDF</span>
                      </GVTooltip>
                    </SegmentedControl.Item>
                  </SegmentedControl.Root>
                </div>
              </>
            )}
            <Separator.Root />
            <div className={styles.footer}>
              <p className={styles.footerError}>{rangeErrorText}</p>
              <GVTooltip
                delayDuration={TOOLTIP_DELAY_DURATION}
                title={tooltip}
                disableHoverListener={!tooltip}
                side="top"
                container={tooltipContainer}
              >
                <Button.Root
                  type="button"
                  id="user-save-button"
                  data-testid="audit-trail-export-button"
                  isDisabled={ctaDisabled}
                  onClick={() => handleSubmit()}
                  label={ctaBusy ? 'Processing...' : ctaLabel}
                  elementStart={ctaBusy ? <Spinner.Root /> : emailExport ? <Mail /> : <Download />}
                />
              </GVTooltip>
            </div>
          </>
        )}
      </Formik>
    );
  }

  return (
    <MyAccountCard
      className={cx(styles.root, { fullHistory: scope === 'fullHistory' })}
      contentClassName={styles.content}
      title={
        <div className={styles.titleBlock} ref={setTooltipContainer}>
          <h2 className={styles.title} id="name-title">
            Generate Audit Trail
          </h2>
          {subtitle}
        </div>
      }
    >
      {cardBody}
    </MyAccountCard>
  );
};

export default General;
